import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { GitHub as GitHubIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { Release, ReleaseType } from '../types';

interface ReleaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<Omit<Release, 'id' | 'status'>>) => void;
  initialData?: Release;
  githubRepoUrl?: string;
}

const ReleaseDialog: React.FC<ReleaseDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  githubRepoUrl
}) => {
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [releaseType, setReleaseType] = useState<ReleaseType>('Development');
  const [previewTab, setPreviewTab] = useState(0);
  useEffect(() => {
    if (initialData) {
      setVersion(initialData.version);
      setDescription(initialData.description || '');
      setReleaseDate(initialData.releaseDate || '');
      setReleaseType(initialData.releaseType || 'Development');
    } else {
      setVersion('');
      setDescription('');
      setReleaseDate('');
      setReleaseType('Development');
    }
  }, [initialData, open]);

  const handleSave = () => {
    const data: any = {
      version,
      description,
      releaseType
    };
    if (releaseDate) {
      data.releaseDate = releaseDate;
    }
    onSave(data);
    handleClose();
  };

  const handleClose = () => {
    setVersion('');
    setDescription('');
    setReleaseDate('');
    setReleaseType('Development');
    onClose();
  };

  const getReleaseUrl = () => {
    if (githubRepoUrl && version) {
      return `${githubRepoUrl}/tag/${version}`;
    }
    return null;
  };

  const releaseUrl = getReleaseUrl();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          width: { xs: 'calc(100% - 32px)', sm: '100%' }
        }
      }}
    >
      <DialogTitle>{initialData ? 'Edit Release' : 'New Release'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            fullWidth
            required
            placeholder="v1.0.0"
          />
          {releaseUrl && (
            <Chip
              icon={<GitHubIcon />}
              label="View on GitHub"
              component="a"
              href={releaseUrl}
              target="_blank"
              clickable
              sx={{ alignSelf: 'flex-start' }}
            />
          )}
          <FormControl fullWidth required>
            <InputLabel>Environment / Type</InputLabel>
            <Select
              value={releaseType}
              onChange={(e) => setReleaseType(e.target.value as ReleaseType)}
              label="Environment / Type"
            >
              <MenuItem value="Development">Development</MenuItem>
              <MenuItem value="Staging">Staging</MenuItem>
              <MenuItem value="Production">Production</MenuItem>
            </Select>
          </FormControl>
          <Box>
            <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)} sx={{ mb: 1 }}>
              <Tab label="Edit" />
              <Tab label="Preview" />
            </Tabs>
            {previewTab === 0 ? (
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder="Supports Markdown formatting"
              />
            ) : (
              <Box sx={{ 
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                p: 2,
                minHeight: '120px',
                '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mt: 2, mb: 1 },
                '& h2': { fontSize: '1.3rem', fontWeight: 'bold', mt: 2, mb: 1 },
                '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mt: 1.5, mb: 0.5 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 2, mb: 1 },
                '& li': { mb: 0.5 },
                '& code': { 
                  backgroundColor: '#f5f5f5', 
                  padding: '2px 6px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em'
                },
                '& pre': { 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  mb: 1
                },
                '& a': { color: 'primary.main', textDecoration: 'underline' }
              }}>
                {description ? (
                  <ReactMarkdown>{description}</ReactMarkdown>
                ) : (
                  <Typography color="text.secondary" fontStyle="italic">
                    No description provided
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <TextField
            label={initialData?.status === 'Planned' ? 'Expected Release Date' : 'Release Date'}
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!version.trim() || !description.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseDialog;
